import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '@/components/shared/Navigation';
import Footer from '@/components/shared/Footer';
import { TrendingUp, Users, Zap, Target, Shield, Clock } from 'lucide-react';

const HomePage = () => {
  return (
    <div className="min-h-screen bg-[#F9FAFB] font-sans">
      <Navigation />

      {/* Hero Section - Enhanced */}
      <section className="bg-gradient-to-br from-[#0A2540] to-[#0d3355]">
        <div className="max-w-7xl mx-auto px-6 py-28">
          <div className="max-w-4xl">
            <div className="inline-block mb-4 px-4 py-2 bg-[#4A6FA5]/20 rounded-full">
              <span className="text-[#4A6FA5] text-sm font-semibold">INSTITUTIONAL CRE SUPPORT</span>
            </div>
            <h1 className="text-6xl font-semibold text-white mb-6 leading-tight">
              Offshore Analyst-Grade Support for U.S. Commercial Real Estate
            </h1>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed">
              Financial modeling, underwriting, lease abstraction, and investment analysis delivered with institutional precision. 
              Scale your analysis capacity without compromising quality.
            </p>
            <div className="flex gap-4">
              <Link
                to="/contact"
                className="px-8 py-4 bg-[#4A6FA5] text-white font-semibold rounded hover:bg-[#3d5a85] transition-colors shadow-lg"
              >
                Receive a Quote
              </Link>
              <Link
                to="/services"
                className="px-8 py-4 bg-white text-[#0A2540] font-semibold rounded border-2 border-white hover:bg-gray-100 transition-colors"
              >
                Explore Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-[#0A2540] mb-2">50+</div>
              <div className="text-[#2E2E2E]">Asset Types Analyzed</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#0A2540] mb-2">$5B+</div>
              <div className="text-[#2E2E2E]">Deal Volume Supported</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#0A2540] mb-2">24/7</div>
              <div className="text-[#2E2E2E]">Time-Zone Coverage</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#0A2540] mb-2">60%</div>
              <div className="text-[#2E2E2E]">Cost Savings vs. Onshore</div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition - Enhanced */}
      <section className="bg-[#F9FAFB] py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-semibold text-[#0A2540] mb-4">
              Why Leading CRE Firms Choose PulseShift
            </h2>
            <p className="text-xl text-[#2E2E2E] max-w-3xl mx-auto">
              We operate as an extension of your investment team, delivering institutional-quality work at offshore economics.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-12">
            <div className="bg-white p-8 rounded-lg border border-gray-200">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0A2540] rounded-lg mb-6">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-[#0A2540] mb-4">
                Institutional Quality Execution
              </h3>
              <p className="text-[#2E2E2E] leading-relaxed mb-4">
                Every deliverable meets U.S. institutional standards. Our analysts are trained on ARGUS, 
                pro forma conventions, and asset-specific underwriting requirements.
              </p>
              <ul className="space-y-2 text-sm text-[#2E2E2E]">
                <li className="flex items-start">
                  <span className="text-[#4A6FA5] mr-2">✓</span>
                  <span>Investment committee ready</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#4A6FA5] mr-2">✓</span>
                  <span>Lender-grade accuracy</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#4A6FA5] mr-2">✓</span>
                  <span>Process-driven consistency</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg border border-gray-200">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0A2540] rounded-lg mb-6">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-[#0A2540] mb-4">
                Cost-Efficient Offshore Support
              </h3>
              <p className="text-[#2E2E2E] leading-relaxed mb-4">
                Access dedicated analyst resources at 40-60% of onshore costs. Scale capacity on demand 
                without long-term hiring commitments.
              </p>
              <ul className="space-y-2 text-sm text-[#2E2E2E]">
                <li className="flex items-start">
                  <span className="text-[#4A6FA5] mr-2">✓</span>
                  <span>No recruitment overhead</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#4A6FA5] mr-2">✓</span>
                  <span>Flexible engagement models</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#4A6FA5] mr-2">✓</span>
                  <span>Rapid team scaling</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg border border-gray-200">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0A2540] rounded-lg mb-6">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-[#0A2540] mb-4">
                Seamless Integration with U.S. Teams
              </h3>
              <p className="text-[#2E2E2E] leading-relaxed mb-4">
                Time-zone aligned workflows with direct access to your dedicated analyst pod. 
                We integrate with your tools, templates, and processes.
              </p>
              <ul className="space-y-2 text-sm text-[#2E2E2E]">
                <li className="flex items-start">
                  <span className="text-[#4A6FA5] mr-2">✓</span>
                  <span>U.S. business hours coverage</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#4A6FA5] mr-2">✓</span>
                  <span>Your tools and templates</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#4A6FA5] mr-2">✓</span>
                  <span>Long-term team continuity</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do - Enhanced */}
      <section className="bg-white py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-semibold text-[#0A2540] mb-6">
                Analyst-Grade Output, Not Freelancer Work
              </h2>
              <div className="space-y-4 text-lg text-[#2E2E2E] leading-relaxed">
                <p>
                  PulseShift Realty Partners provides dedicated offshore analyst support to U.S.-based commercial 
                  real estate investors, private equity firms, lenders, and advisory platforms.
                </p>
                <p>
                  We're not a freelancer marketplace. We're not a VA service. We're an offshore analyst platform 
                  built by finance professionals who understand institutional standards.
                </p>
                <p>
                  Our teams operate as an extension of your investment and acquisitions teams — time-zone aligned, 
                  process-driven, and trained on your specific workflows.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-[#F9FAFB] p-6 rounded-lg">
                <Target className="w-10 h-10 text-[#0A2540] mb-4" />
                <h4 className="font-semibold text-[#0A2540] mb-2">Precision</h4>
                <p className="text-sm text-[#2E2E2E]">Accuracy-first approach to all financial analysis</p>
              </div>
              <div className="bg-[#F9FAFB] p-6 rounded-lg">
                <Shield className="w-10 h-10 text-[#0A2540] mb-4" />
                <h4 className="font-semibold text-[#0A2540] mb-2">Quality Control</h4>
                <p className="text-sm text-[#2E2E2E]">Multi-layer review process on every deliverable</p>
              </div>
              <div className="bg-[#F9FAFB] p-6 rounded-lg">
                <Clock className="w-10 h-10 text-[#0A2540] mb-4" />
                <h4 className="font-semibold text-[#0A2540] mb-2">Fast Turnaround</h4>
                <p className="text-sm text-[#2E2E2E]">24-hour coverage for urgent deal requirements</p>
              </div>
              <div className="bg-[#F9FAFB] p-6 rounded-lg">
                <Users className="w-10 h-10 text-[#0A2540] mb-4" />
                <h4 className="font-semibold text-[#0A2540] mb-2">Dedicated Teams</h4>
                <p className="text-sm text-[#2E2E2E]">Long-term pod assignments for consistency</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-[#0A2540] to-[#0d3355] py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-semibold text-white mb-6">
            Ready to Scale Your Analysis Capacity?
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            Request a confidential consultation to discuss your underwriting and analysis requirements. 
            We'll provide a tailored proposal within 24 hours.
          </p>
          <Link
            to="/contact"
            className="inline-block px-8 py-4 bg-[#4A6FA5] text-white font-semibold rounded-lg hover:bg-[#3d5a85] transition-colors shadow-lg"
          >
            Schedule Consultation
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
